// src/components/search/SearchFacets.tsx
import { Box, IconButton, Skeleton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FacetFilter from "@/components/FacetChipFilter";
import { NONE_VALUE } from "@/search/constants";
import type { AddressPage } from "@/search/types";

interface SearchFacetsProps {
  facetPage: AddressPage | null;
  postcodeFacet: string[];
  cityFacet: string[];
  localityFacet: string[];
  statusFacet: string[];
  anyFacetActive: boolean;
  isLoading: boolean;
  onPostcodeFacetChange: (values: string[]) => void;
  onCityFacetChange: (values: string[]) => void;
  onLocalityFacetChange: (values: string[]) => void;
  onStatusFacetChange: (values: string[]) => void;
  onClearAllFacets: () => void;
}

/**
 * Get a display label for a facet value, handling empty/null values.
 */
function facetLabel(value: string, fallback: string): string {
  return value === NONE_VALUE || value === "" ? `No ${fallback}` : value;
}

export default function SearchFacets({
  facetPage,
  postcodeFacet,
  cityFacet,
  localityFacet,
  statusFacet,
  anyFacetActive,
  isLoading,
  onPostcodeFacetChange,
  onCityFacetChange,
  onLocalityFacetChange,
  onStatusFacetChange,
  onClearAllFacets,
}: SearchFacetsProps) {
  const postcodeCounts = facetPage?.postcode_counts ?? [];
  const cityFacetOptions = facetPage?.post_town_counts ?? [];
  const localityFacetOptions = facetPage?.dependent_locality_counts ?? [];
  const statusFacetOptions = facetPage?.status_counts ?? [];

  const showSkeletons = isLoading && !facetPage;

  // Don't render anything if no facets available and not loading
  if (!showSkeletons && !postcodeCounts.length && !cityFacetOptions.length && 
      !localityFacetOptions.length && !statusFacetOptions.length && !anyFacetActive) {
    return null;
  }

  return (
    <Box
      sx={{
        px: 2,
        pb: 1,
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
      }}
    >
      {showSkeletons ? (
        <>
          <Skeleton variant="rounded" width={140} height={34} />
          <Skeleton variant="rounded" width={140} height={34} />
        </>
      ) : (
        <>
          {postcodeCounts.length > 0 && (
            <FacetFilter
              title="Postcode"
              options={postcodeCounts.map((p) => ({
                value: p.value,
                label: `${p.value} (${p.count})`,
              }))}
              selected={postcodeFacet}
              onChange={onPostcodeFacetChange}
            />
          )}

          {cityFacetOptions.length > 0 && (
            <FacetFilter
              title="City"
              options={cityFacetOptions.map((pt) => {
                const value = pt?.value ?? NONE_VALUE;
                return {
                  value,
                  label: `${facetLabel(value, "city")} (${pt?.count ?? 0})`,
                };
              })}
              selected={cityFacet}
              onChange={onCityFacetChange}
            />
          )}

          {localityFacetOptions.length > 0 && (
            <FacetFilter
              title="Locality"
              options={localityFacetOptions.map((loc) => {
                const value = loc?.value ?? NONE_VALUE;
                return {
                  value,
                  label: `${facetLabel(value, "locality")} (${loc?.count ?? 0})`,
                };
              })}
              selected={localityFacet}
              onChange={onLocalityFacetChange}
            />
          )}

          {statusFacetOptions.length > 0 && (
            <FacetFilter
              title="Status"
              options={statusFacetOptions.map((s) => ({
                value: s?.value ?? "",
                label: `${s?.label ?? facetLabel(s?.value ?? "", "status")} (${s?.count ?? 0})`,
              }))}
              selected={statusFacet}
              onChange={onStatusFacetChange}
            />
          )}
        </>
      )}

      {anyFacetActive && (
        <Tooltip title="Clear all facet filters">
          <IconButton
            size="small"
            onClick={onClearAllFacets}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              ml: 0.5,
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
